/**
 * The loan tape's Map, as data: which cards the graph draws, where, and the
 * arrows between them.
 *
 * The picture reads left to right, one row per standardized table: the raw
 * tabs that fill it (one from the new file, one from the example file the tape
 * was set up on), the table, the join that attaches it to the loan, and the
 * tape the loan becomes. Nothing flows backwards.
 *
 * Two sources feed it. A run recorded since the extractor wrote a `schema`
 * section carries the tables' fields and the example file's side of the
 * mapping; an earlier run carries only the flattened `nodes`/`edges`, and the
 * same picture is rebuilt from those and the columns' own lineage, without
 * the example file. Either way everything here is read off the run: nothing
 * is assumed about which tables a schema has.
 */

import type {
	LoanTapePreviewColumn,
	SchemaGraphBlock,
	SchemaMapEntity,
	SchemaMapJoin,
} from "../Chat/Chat.types";
import { columnOrigin, countOrigins, type OriginCounts } from "./loanTapeColumns";

export type TapeMapKind =
	/** A tab of the new file that fills a table. */
	| "tab"
	/** A tab of the example file that fills the same table. */
	| "example-tab"
	/** A tab, of either file, that no table reads. */
	| "unused-tab"
	| "table"
	| "tape"
	/** Columns counted from the tape's own settings, not read from a file. */
	| "settings"
	/** A tape column with nothing in the mapping behind it. */
	| "unmapped";

export interface TapeMapField {
	name: string;
	column: string | null;
	exampleColumn: string | null;
}

/** What the side panel says about a card. Only the kind's own facts are set. */
export interface TapeMapDetail {
	/** table · tab · example-tab: the standardized table concerned. */
	table?: string;
	grain?: string;
	grainKey?: string | null;
	/** table: the tabs that fill it. tab: the other file's tab for the table. */
	tab?: string | null;
	exampleTab?: string | null;
	fields?: TapeMapField[];
	/** table: the joins it takes part in, as "on a = b". */
	joins?: string[];
	/** How many fields the table has, and how many the new file fills. */
	fieldTotal?: number;
	fieldsFilled?: number;
	/** False when `fields` is only the fields the tape reads (an earlier run). */
	fieldsComplete?: boolean;
	/** table · settings · tab: tape columns read from here. */
	columns?: string[];
	/** unused-tab: which file it is in. */
	file?: "new" | "example";
	/** tape: how its columns were made. */
	counts?: OriginCounts;
	loans?: number | null;
}

export interface TapeMapNode {
	id: string;
	kind: TapeMapKind;
	label: string;
	sublabel: string;
	badge?: string;
	x: number;
	y: number;
	width: number;
	height: number;
	detail: TapeMapDetail;
}

export interface TapeMapEdge {
	id: string;
	source: string;
	target: string;
	label: string;
	/** On this run's path: the new file, through the tables, to the tape. */
	onPath: boolean;
	/** Labelled at rest. The rest say theirs when an end is selected. */
	showLabel: boolean;
	/** Runs down from the card above rather than across from the one beside. */
	vertical?: boolean;
}

/** A heading over one column of the picture. */
export interface TapeMapCaption {
	id: string;
	label: string;
	x: number;
	y: number;
}

export interface TapeMap {
	nodes: TapeMapNode[];
	edges: TapeMapEdge[];
	captions: TapeMapCaption[];
	/** The table the others join onto, as the reader knows it: "Loan". */
	spineLabel: string | null;
	/** Whether the run recorded the example file's side of the mapping. */
	hasExample: boolean;
	/** Whether the tables join onto one of them, which then becomes the tape. */
	hasSpine: boolean;
	tableCount: number;
	fieldCount: number;
}

export interface TapeMapInput {
	graph: SchemaGraphBlock;
	columns: readonly LoanTapePreviewColumn[];
	loans: number | null;
	/**
	 * What to call each file on its tabs: the deal it sits in ("Auto Loan ABS").
	 * Without one a tab says "new file" or "example file".
	 */
	sourceLabel?: string | null;
	exampleLabel?: string | null;
}

// ---- geometry ---------------------------------------------------------------

const TAB = { width: 204, height: 50 };
const TABLE = { width: 232, height: 64 };
const TAPE = { width: 200, height: 68 };
const SIDE = { width: 204, height: 54 };
/** Row pitch: a table's two tabs, stacked, plus air. */
const ROW = 136;
const TAB_GAP = 8;
const X_TAB = 0;
const X_TABLE = 270;
/** The spine's own tabs sit one column in, beside it. */
const X_SPINE_TAB = X_TABLE + 14;
const X_SPINE = 630;
const X_OUT = 962;

// ---- names ------------------------------------------------------------------

/** Schema names a reader should not have to decode. */
const TABLE_NAMES: Record<string, string> = {
	app: "Application",
	credit: "Credit pull",
};

/** "canonical_loan" → "Loan". The schema's own name stays in the panel. */
export function tableLabel(name: string): string {
	const bare = name.replace(/^canonical_/, "");
	const known = TABLE_NAMES[bare];
	if (known) return known;
	const words = bare.replace(/_/g, " ").trim();
	return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}

const plural = (count: number, word: string): string =>
	`${count} ${word}${count === 1 ? "" : "s"}`;

// ---- reading the run --------------------------------------------------------

interface Table {
	name: string;
	grain: string;
	grainKey: string | null;
	tab: string | null;
	exampleTab: string | null;
	fields: TapeMapField[];
	/**
	 * An earlier run names only the fields its tape reads, but states the
	 * table's own counts: how many fields it has and how many the file fills.
	 * Set, they are the truth and `fields` is a partial list.
	 */
	fieldTotal?: number;
	fieldsFilled?: number;
}

interface Schema {
	tables: Table[];
	joins: SchemaMapJoin[];
	tabs: string[];
	exampleTabs: string[];
}

const fromEntity = (entity: SchemaMapEntity): Table => ({
	name: entity.name,
	grain: entity.grain,
	grainKey: entity.grain_key,
	tab: entity.source_tab,
	exampleTab: entity.example_tab,
	fields: entity.fields.map((field) => ({
		name: field.name,
		column: field.column,
		exampleColumn: field.example_column,
	})),
});

/**
 * The schema of a run recorded before the `schema` section existed, from what
 * that run does carry: the flattened graph for the tables, tabs and joins, and
 * the columns' lineage for the fields the tape reads.
 */
function schemaFromGraph(
	graph: SchemaGraphBlock,
	columns: readonly LoanTapePreviewColumn[],
): Schema {
	const name = (id: string) => id.replace(/^(ent|raw|out):/, "");
	const entityIds = new Set(
		graph.nodes.filter((node) => node.category === "entity").map((node) => node.id),
	);
	const rawName = new Map(
		graph.nodes
			.filter((node) => node.category === "raw")
			.map((node) => [node.id, node.name]),
	);

	const fieldsOf = new Map<string, Map<string, string>>();
	columns.forEach((column) => {
		column.lineage?.sources.forEach((source) => {
			const [table, field] = source.canonical_field.split(".");
			if (!table || !field) return;
			if (!fieldsOf.has(table)) fieldsOf.set(table, new Map());
			fieldsOf.get(table)?.set(field, source.column);
		});
	});

	const tables = graph.nodes
		.filter((node) => node.category === "entity")
		.map((node): Table => {
			// "one row per funded loan · key: loan_id · source tab: … · 8/8 fields mapped"
			const parts = (node.detail ?? "").split(" · ");
			const key = parts.find((part) => part.startsWith("key: "));
			const coverage = /(\d+)\/(\d+) fields mapped/.exec(node.detail ?? "");
			const feed = graph.edges.find(
				(edge) => edge.target === node.id && rawName.has(edge.source),
			);
			return {
				name: node.name,
				grain: parts[0] ?? "",
				grainKey: key ? key.slice("key: ".length) : null,
				tab: feed ? (rawName.get(feed.source) ?? null) : null,
				exampleTab: null,
				fields: Array.from(fieldsOf.get(node.name) ?? []).map(
					([field, column]) => ({ name: field, column, exampleColumn: null }),
				),
				fieldsFilled: coverage ? Number(coverage[1]) : undefined,
				fieldTotal: coverage ? Number(coverage[2]) : undefined,
			};
		});

	return {
		tables,
		joins: graph.edges
			.filter((edge) => entityIds.has(edge.source) && entityIds.has(edge.target))
			.map((edge) => ({
				source: name(edge.source),
				target: name(edge.target),
				on: edge.label ?? "",
			})),
		tabs: Array.from(rawName.values()),
		exampleTabs: [],
	};
}

function readSchema(
	graph: SchemaGraphBlock,
	columns: readonly LoanTapePreviewColumn[],
): Schema {
	const schema = graph.schema;
	if (!schema || schema.entities.length === 0) {
		return schemaFromGraph(graph, columns);
	}
	return {
		tables: schema.entities.map(fromEntity),
		joins: schema.joins,
		tabs: schema.source_tabs,
		exampleTabs: schema.example_tabs,
	};
}

const fieldTotalOf = (table: Table): number =>
	Math.max(table.fieldTotal ?? 0, table.fields.length);

/** How many of the table's fields the new file fills. */
const fieldsFilledOf = (table: Table): number =>
	table.fieldsFilled ?? table.fields.filter((field) => field.column).length;

/** Whether `fields` names every field the table has. */
const listsEveryField = (table: Table): boolean =>
	table.fields.length >= fieldTotalOf(table);

/**
 * The table the others join onto: the most-joined. Null when the run records
 * no join between several tables: which of them becomes the tape is then not
 * something the run says, and the first in the list is not an answer.
 */
function spineOf(schema: Schema): string | null {
	if (schema.tables.length > 1 && schema.joins.length === 0) return null;
	const degree = new Map(schema.tables.map((table) => [table.name, 0]));
	schema.joins.forEach((join) => {
		[join.source, join.target].forEach((end) => {
			if (degree.has(end)) degree.set(end, (degree.get(end) ?? 0) + 1);
		});
	});
	let spine: string | null = null;
	let best = -1;
	schema.tables.forEach((table) => {
		const count = degree.get(table.name) ?? 0;
		if (count > best) {
			best = count;
			spine = table.name;
		}
	});
	return spine;
}

/** "application_ref = application_ref" → "application_ref". */
const joinKey = (on: string): string => {
	const [left, right] = on.split("=").map((side) => side.trim());
	if (!right || left === right) return left ?? "";
	return `${left} = ${right}`;
};

/** The joining table's own key: what fits on an arrow. The panel has both. */
const joinLabel = (on: string): string => on.split("=")[0]?.trim() ?? "";

// ---- the map ----------------------------------------------------------------

export function buildTapeMap({
	graph,
	columns,
	loans,
	sourceLabel,
	exampleLabel,
}: TapeMapInput): TapeMap {
	const sourceName = sourceLabel?.trim() || "new file";
	const exampleName = exampleLabel?.trim() || "example file";
	const schema = readSchema(graph, columns);
	const spine = spineOf(schema);
	const nodes: TapeMapNode[] = [];
	const edges: TapeMapEdge[] = [];
	const hasExample = schema.tables.some((table) => table.exampleTab);

	// The spine in the middle row, the tables that join onto it around it.
	const others = schema.tables.filter((table) => table.name !== spine);
	const above = others.slice(0, Math.ceil(others.length / 2));
	const below = others.slice(above.length);
	const spineTable = schema.tables.find((table) => table.name === spine);
	const rows = [...above, ...(spineTable ? [spineTable] : []), ...below];
	const rowCentre = (index: number) => index * ROW + ROW / 2;
	// With no spine the tape sits level with the middle of the column.
	const spineCentre = spineTable
		? rowCentre(above.length)
		: (Math.max(rows.length, 1) * ROW) / 2;

	// Tape columns by the table and the tab they read.
	const columnsOfTable = new Map<string, string[]>();
	const columnsOfTab = new Map<string, string[]>();
	const push = (map: Map<string, string[]>, key: string, header: string) => {
		const list = map.get(key) ?? [];
		if (!list.includes(header)) list.push(header);
		map.set(key, list);
	};
	columns.forEach((column) => {
		column.lineage?.sources.forEach((source) => {
			const table = source.canonical_field.split(".")[0];
			if (table) push(columnsOfTable, table, column.header);
			if (source.tab) push(columnsOfTab, source.tab, column.header);
		});
	});

	const tableId = (name: string) => `table:${name}`;
	const edge = (
		source: string,
		target: string,
		label: string,
		onPath: boolean,
		showLabel = false,
	) => {
		edges.push({ id: `${source}->${target}`, source, target, label, onPath, showLabel });
	};

	rows.forEach((table, index) => {
		const centre = rowCentre(index);
		const isSpine = table.name === spine;
		const label = tableLabel(table.name);
		const joins = schema.joins
			.filter((join) => join.source === table.name || join.target === table.name)
			.map((join) => {
				const other = join.source === table.name ? join.target : join.source;
				return `${tableLabel(other)}, on ${joinKey(join.on)}`;
			});

		nodes.push({
			id: tableId(table.name),
			kind: "table",
			label,
			sublabel: table.grain,
			badge: plural(fieldTotalOf(table), "field").toUpperCase(),
			x: isSpine ? X_SPINE : X_TABLE,
			y: centre - TABLE.height / 2,
			...TABLE,
			detail: {
				table: table.name,
				grain: table.grain,
				grainKey: table.grainKey,
				tab: table.tab,
				exampleTab: table.exampleTab,
				fields: table.fields,
				fieldTotal: fieldTotalOf(table),
				fieldsFilled: fieldsFilledOf(table),
				fieldsComplete: listsEveryField(table),
				joins,
				columns: columnsOfTable.get(table.name) ?? [],
			},
		});

		// Its tabs: stacked when both files fill the table, centred when one does.
		const tabX = isSpine ? X_SPINE_TAB : X_TAB;
		const both = Boolean(table.tab && table.exampleTab);
		const coverage = {
			fieldTotal: fieldTotalOf(table),
			fieldsFilled: fieldsFilledOf(table),
			fieldsComplete: listsEveryField(table),
		};
		if (table.exampleTab) {
			const id = `example-tab:${table.name}`;
			nodes.push({
				id,
				kind: "example-tab",
				label: table.exampleTab,
				sublabel: exampleName,
				x: tabX,
				y: both ? centre - TAB.height - TAB_GAP / 2 : centre - TAB.height / 2,
				...TAB,
				detail: {
					table: table.name,
					tab: table.tab,
					exampleTab: table.exampleTab,
					fields: table.fields,
					...coverage,
				},
			});
			edge(id, tableId(table.name), "maps to", false);
		}
		if (table.tab) {
			const id = `tab:${table.name}`;
			nodes.push({
				id,
				kind: "tab",
				label: table.tab,
				sublabel: sourceName,
				x: tabX,
				y: both ? centre + TAB_GAP / 2 : centre - TAB.height / 2,
				...TAB,
				detail: {
					table: table.name,
					tab: table.tab,
					exampleTab: table.exampleTab,
					fields: table.fields,
					...coverage,
					columns: columnsOfTab.get(table.tab) ?? [],
				},
			});
			edge(
				id,
				tableId(table.name),
				`${coverage.fieldsFilled} of ${plural(coverage.fieldTotal, "field")}`,
				true,
			);
		}
	});

	// Joins run onto the spine. One between two other tables is drawn as it is.
	schema.joins.forEach((join) => {
		const known = (name: string) => schema.tables.some((table) => table.name === name);
		if (!known(join.source) || !known(join.target)) return;
		const towardSpine = join.source === spine ? join.target : join.source;
		const target = join.source === spine || join.target === spine ? spine : join.target;
		if (!target) return;
		edge(tableId(towardSpine), tableId(target), `on ${joinLabel(join.on)}`, true, true);
	});

	// The tape, fed by the spine.
	nodes.push({
		id: "tape",
		kind: "tape",
		label: "Loan Tape",
		sublabel: [
			loans !== null ? plural(loans, "loan") : null,
			plural(columns.length, "column"),
		]
			.filter(Boolean)
			.join(" · "),
		x: X_OUT,
		y: spineCentre - TAPE.height / 2,
		...TAPE,
		detail: { counts: countOrigins(columns), loans },
	});
	if (spine) {
		edge(tableId(spine), "tape", plural(columns.length, "column"), true, true);
	} else {
		// No recorded join: draw only what the columns' own lineage supports,
		// each table into the tape with the columns that read it.
		rows.forEach((table) => {
			const read = columnsOfTable.get(table.name) ?? [];
			if (read.length > 0) {
				edge(tableId(table.name), "tape", plural(read.length, "column"), true, true);
			}
		});
	}

	// Columns counted from the tape's own settings rather than read from a file.
	const fromSettings = columns
		.filter((column) => column.lineage?.kind === "derived" && column.lineage.sources.length === 0)
		.map((column) => column.header);
	if (fromSettings.length > 0) {
		nodes.push({
			id: "settings",
			kind: "settings",
			label: "Tape settings",
			sublabel: "not read from a file",
			// Straight above the tape, its arrow running down into it.
			x: X_OUT,
			y: spineCentre - TAPE.height / 2 - SIDE.height - 56,
			...SIDE,
			detail: { columns: fromSettings },
		});
		edges.push({
			id: "settings->tape",
			source: "settings",
			target: "tape",
			label: plural(fromSettings.length, "column"),
			onPath: false,
			showLabel: false,
			vertical: true,
		});
	}

	// Columns with nothing behind them, hung off the table whose field they
	// are named for in the SQL when that can be told, and off the tape if not.
	const fieldOwner = (header: string): { table: string; field: string } | null => {
		const escaped = header.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const match = new RegExp(`\\b(\\w+)\\.(\\w+)\\s+AS\\s+"${escaped}"`).exec(
			graph.sql ?? "",
		);
		if (!match) return null;
		const [, alias, field] = match;
		// The alias the SQL reads it through says whose it is ("collat" for
		// canonical_collateral), so it decides whenever it names one table. A
		// field name is only a fallback: an earlier run lists a table's fields
		// partially, and the one table that happens to list this name need not
		// be the one the SQL read.
		const byAlias = schema.tables.filter((table) =>
			table.name.replace(/^canonical_/, "").startsWith(alias.toLowerCase()),
		);
		const byField = schema.tables.filter((table) =>
			table.fields.some((entry) => entry.name === field),
		);
		const owners = byAlias.length === 1 ? byAlias : byField;
		return owners.length === 1 ? { table: owners[0].name, field } : null;
	};
	const stacked = new Map<string, number>();
	columns
		.filter((column) => columnOrigin(column) === "unmapped")
		.forEach((column) => {
			const owned = fieldOwner(column.header);
			const owner = owned?.table ?? null;
			const anchor = owner ? tableId(owner) : "tape";
			const count = stacked.get(anchor) ?? 0;
			stacked.set(anchor, count + 1);
			const row = owner ? rows.findIndex((table) => table.name === owner) : -1;
			const below = owner === spine || row < 0;
			const id = `unmapped:${column.header}`;
			nodes.push({
				id,
				kind: "unmapped",
				label: column.header,
				sublabel: "no source recorded",
				x: below ? X_OUT : X_SPINE + 10,
				y: below
					? spineCentre + TAPE.height / 2 + 60 + count * (SIDE.height + 12)
					: rowCentre(row) - SIDE.height / 2 + count * (SIDE.height + 12),
				...SIDE,
				detail: { table: owner ?? undefined, columns: [column.header] },
			});
			edge(
				anchor === "tape" && spine ? tableId(spine) : anchor,
				id,
				owned?.field ?? "unmapped",
				false,
				true,
			);
		});

	// Tabs no table reads, in the tab column, in the rows the spine leaves free.
	const usedTabs = new Set(schema.tables.map((table) => table.tab));
	const usedExample = new Set(schema.tables.map((table) => table.exampleTab));
	const unused = [
		...schema.exampleTabs
			.filter((tab) => !usedExample.has(tab))
			.map((tab) => ({ tab, file: "example" as const })),
		...schema.tabs
			.filter((tab) => !usedTabs.has(tab))
			.map((tab) => ({ tab, file: "new" as const })),
	];
	// Two fit beside the spine; the rest go under the last row.
	const slot = (index: number): number => {
		if (spineTable && index < 2) {
			return index === 0
				? spineCentre - TAB.height - TAB_GAP / 2
				: spineCentre + TAB_GAP / 2;
		}
		const extra = spineTable ? index - 2 : index;
		return rows.length * ROW + extra * (TAB.height + TAB_GAP);
	};
	unused.forEach(({ tab, file }, index) => {
		nodes.push({
			id: `unused:${file}:${tab}`,
			kind: "unused-tab",
			label: tab,
			sublabel: `${file === "example" ? exampleName : sourceName} · not used`,
			x: X_TAB,
			y: slot(index),
			...TAB,
			detail: { file },
		});
	});

	// A heading over each column, as the picture is read.
	const spineLabel = spine ? tableLabel(spine) : null;
	const captionY = -40;
	const captions: TapeMapCaption[] = [
		{ id: "caption:tabs", label: "Raw tabs", x: X_TAB, y: captionY },
		{ id: "caption:tables", label: "Standardized tables", x: X_TABLE, y: captionY },
		...(spineLabel && others.length > 0
			? [
					{
						id: "caption:spine",
						label: `Joined onto the ${spineLabel.toLowerCase()}`,
						x: X_SPINE,
						y: captionY,
					},
				]
			: []),
		{ id: "caption:tape", label: "The tape", x: X_OUT, y: captionY },
	];

	return {
		nodes,
		edges,
		captions,
		spineLabel,
		hasExample,
		hasSpine: spine !== null,
		tableCount: schema.tables.length,
		fieldCount: schema.tables.reduce((sum, table) => sum + fieldTotalOf(table), 0),
	};
}

/**
 * What a fit of the canvas depends on: which cards there are and where the
 * layout put them. Two runs can have the same cards in different places (a
 * different spine, an example file one has and the other does not), and the
 * viewport fitted to one would clip the other. A card the reader drags does
 * not change it: these are the layout's positions, not the canvas's.
 */
export function layoutSignature(map: TapeMap): string {
	return [
		...map.nodes.map(
			(node) => `${node.id}@${node.x},${node.y},${node.width}x${node.height}`,
		),
		...map.captions.map((caption) => `${caption.id}@${caption.x},${caption.y}`),
	].join("|");
}

/** The cards an arrow joins to `id`, and `id` itself. */
export function neighbourhood(map: TapeMap, id: string): Set<string> {
	const near = new Set([id]);
	map.edges.forEach((edge) => {
		if (edge.source === id) near.add(edge.target);
		if (edge.target === id) near.add(edge.source);
	});
	return near;
}
